/**
 * Step 1: Pick a prop firm.
 *
 * Loads every firm via `listFirms()` once on mount and filters by search
 * query. Platform selection lives on the Account step — firms that only
 * support one playable platform pre-fill it here as a convenience, but
 * the visible platform picker is rendered downstream so the Firm step
 * stays about one decision.
 */

import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { ChevronRight, Search, SearchX } from "@/components/ui/icons";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { FirmLogo } from "@/components/firm-logo";
import { firmLogoUrl } from "@/lib/firm-logos";
import { listFirms, type FirmRow } from "@/lib/api/firms";
import type { PlatformType } from "@pfh/shared";
import { PLATFORM_OPTIONS } from "./types";
import type { WizardState } from "./types";

const EASE = [0.16, 1, 0.3, 1] as const;

interface Props {
  state: WizardState;
  marketType?: "forex_cfd" | "futures";
  /** Fires when the operator picks a firm. `platform` is non-null only
   *  when the firm supports exactly one playable platform — the Account
   *  step surfaces a picker otherwise. */
  onSelect: (firm: FirmRow, platform: PlatformType | null) => void;
}

export function StepFirm({ state, marketType, onSelect }: Props) {
  const [allFirms, setAllFirms] = useState<FirmRow[] | null>(null);
  const [query, setQuery] = useState("");
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listFirms()
      .then((firms) => {
        if (!cancelled) setAllFirms(firms);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!allFirms) return [];
    const q = query.trim().toLowerCase();
    return allFirms
      .filter((f) => (marketType ? (f.market_type ?? "forex_cfd") === marketType : true))
      .filter((f) =>
        q ? f.name.toLowerCase().includes(q) || f.id.toLowerCase().includes(q) : true,
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allFirms, marketType, query]);

  const handleFirmClick = (firm: FirmRow) => {
    const raw = firm.supported_platforms ?? [];
    const ready = new Set(
      PLATFORM_OPTIONS.filter((o) => o.driverReady).map((o) => o.type),
    );
    const playable = raw.filter((p): p is PlatformType => ready.has(p as PlatformType));
    // Single-platform firms pre-fill the platform so the Account step
    // can render without asking an obvious question; multi-platform
    // firms leave it null and the Account step surfaces a picker.
    const autoPlatform = playable.length === 1 ? playable[0]! : null;
    onSelect(firm, autoPlatform);
  };

  if (loadError) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
        Failed to load firms: {loadError}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
        <Input
          placeholder="Search firms…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
          className="h-10 pl-9"
        />
      </div>

      <div className="-mx-1.5 grid max-h-[480px] grid-cols-1 gap-3 overflow-y-auto p-1.5 sm:grid-cols-2">
        {!allFirms && (
          <>
            <Skeleton className="h-[84px] w-full rounded-xl" />
            <Skeleton className="h-[84px] w-full rounded-xl" />
            <Skeleton className="h-[84px] w-full rounded-xl" />
            <Skeleton className="h-[84px] w-full rounded-xl" />
          </>
        )}
        <AnimatePresence>
          {allFirms &&
            filtered.map((firm, i) => {
              const selected = state.firm?.id === firm.id;
              const logoSrc = firmLogoUrl(firm.id) ?? "";
              const platformCount = firm.supported_platforms?.length ?? 0;
              return (
                <motion.div
                  key={firm.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    delay: Math.min(i * 0.03, 0.2),
                    duration: 0.35,
                    ease: EASE,
                  }}
                  className="h-full"
                >
                  <Card
                    title={firm.name}
                    className={cn(
                      "group relative h-full cursor-pointer overflow-hidden bg-card/80 py-0 backdrop-blur-sm transition-all duration-200",
                      "hover:-translate-y-0.5 hover:border-accent/40 hover:bg-accent/5 hover:ring-accent/20",
                      selected && "border-accent/60 bg-accent/5 ring-2 ring-accent/40",
                    )}
                    onClick={() => handleFirmClick(firm)}
                  >
                    <CardContent className="flex h-full items-center gap-3.5 p-4">
                      <div
                        className={cn(
                          "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-muted/50 ring-1 ring-inset ring-border/40 transition-colors",
                          selected && "bg-accent/10 ring-accent/30",
                        )}
                      >
                        <FirmLogo
                          src={logoSrc}
                          name={firm.name}
                          className="h-7 w-7"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div
                          className={cn(
                            "truncate text-sm font-semibold text-foreground transition-colors",
                            selected && "text-accent",
                          )}
                        >
                          {firm.name}
                        </div>
                        {platformCount > 0 && (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {platformCount} Platform{platformCount === 1 ? "" : "s"}
                          </p>
                        )}
                      </div>
                      <ChevronRight
                        className={cn(
                          "h-4 w-4 shrink-0 transition-all duration-200",
                          selected
                            ? "text-accent"
                            : "text-muted-foreground/30 group-hover:translate-x-0.5 group-hover:text-accent",
                        )}
                      />
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          {allFirms && filtered.length === 0 && (
            <div className="col-span-full flex flex-col items-center gap-3 rounded-xl border border-dashed border-border/60 bg-muted/10 p-8 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted/50 text-muted-foreground">
                <SearchX className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-medium text-foreground">No firms found</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Try clearing the search or switch account type.
                </p>
              </div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
